import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Space, Spin, Empty } from 'antd';
import { getLicenses, getLicensesByCustomer } from '../services/licenseService';
import { LICENSE_STATUS, LICENSE_TYPES } from '../services/licenseService';
import { useAuth } from '../contexts/AuthContext';
// formatDate is imported from dateUtils but not currently used
// import { formatDate } from '../utils/dateUtils';

export default function CustomerProfileLayer({ customer, onUpdate }) {
    const navigate = useNavigate();
    const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoError, setLogoError] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [licenses, setLicenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    
    // Fetch customer licenses when customer ID changes
    const fetchCustomerLicenses = useCallback(async () => {
        if (!customer?.id) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const { items } = await getLicenses(
                0,          // skip
                100,        // limit
                undefined,  // status
                undefined,  // type
                customer.id,// customerId
                undefined,  // expiresSoon
                undefined   // search
            );
            
            setLicenses(items || []);
        } catch (err) {
            console.error('Error fetching licenses:', err);
            setError('Failed to load licenses');
            toast.error('Failed to load license information');
        } finally {
            setLoading(false);
        }
    }, [customer?.id]);
    
    // Initialize licenses when component mounts or customer changes
    useEffect(() => {
        fetchCustomerLicenses();
    }, [fetchCustomerLicenses]);
    
    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            company_name: customer?.company_name || '',
            company_email: customer?.company_email || '',
            company_phone: customer?.company_phone || '',
            industry: customer?.industry || '',
            website: customer?.website || '',
            status: customer?.status || 'active',
            contact_name: customer?.contact_name || '',
            contact_email: customer?.contact_email || '',
            contact_phone: customer?.contact_phone || '',
            job_title: customer?.job_title || '',
            street_address: customer?.street_address || '',
            city: customer?.city || '',
            state_province: customer?.state_province || '',
            postal_code: customer?.postal_code || '',
            country: customer?.country || '',
            notes: customer?.notes || ''
        }
    });
    
    // Update form and logo preview when customer data changes
    useEffect(() => {
        if (customer) {
            reset({
                company_name: customer.company_name || '',
                company_email: customer.company_email || '',
                company_phone: customer.company_phone || '',
                industry: customer.industry || '',
                website: customer.website || '',
                status: customer.status || 'active',
                contact_name: customer.contact_name || '',
                contact_email: customer.contact_email || '',
                contact_phone: customer.contact_phone || '',
                job_title: customer.job_title || '',
                street_address: customer.street_address || '',
                city: customer.city || '',
                state_province: customer.state_province || '',
                postal_code: customer.postal_code || '',
                country: customer.country || '',
                notes: customer.notes || ''
            });
            
            if (customer?.company_logo) {
                // Convert uploads/filename.jpg to full backend URL
                const filename = customer.company_logo.replace('uploads/', '');
                const backendUrl = process.env.REACT_APP_ROOT_URL || 'http://localhost:8000';
                const staticUrl = `${backendUrl.replace('/api/v1', '')}/static/${filename}`;
                setLogoPreviewUrl(staticUrl);
                setLogoError(false);
            } else if (customer?.logo_url) {
                // Ensure logo_url is absolute
                let logoUrl = customer.logo_url;
                if (logoUrl.startsWith('/static/')) {
                    const backendUrl = process.env.REACT_APP_ROOT_URL || 'http://localhost:8000';
                    logoUrl = `${backendUrl.replace('/api/v1', '')}${logoUrl}`;
                }
                setLogoPreviewUrl(logoUrl);
                setLogoError(false);
            } else {
                setLogoError(true);
            }
        }
    }, [customer, reset]);

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check if file is an image
            if (!file.type.startsWith('image/')) {
                toast.error('Please upload an image file');
                return;
            }
            
            // Check file size (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                toast.error('Image size should be less than 2MB');
                return;
            }
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreviewUrl(reader.result);
                setLogoFile(file);
                setLogoError(false);
            };
            reader.onerror = () => {
                setLogoError(true);
                toast.error('Failed to load image');
            };
            reader.readAsDataURL(file);
        }
    };

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            // Create FormData to handle file upload
            const formData = new FormData();
            
            // Append all non-empty form data
            Object.entries(data).forEach(([key, value]) => {
                // Only append if the value is not null, undefined, or empty string
                if (value !== null && value !== undefined && value !== '') {
                    formData.append(key, value);
                }
            });
            
            // Handle logo file upload
            if (logoFile) {
                // If a new logo file is selected, include it in the form data
                formData.append('company_logo_file', logoFile);
            } else if (customer?.company_logo || customer?.logo_url) {
                // If no new logo is selected but there's an existing logo, include the existing logo URL
                const existingLogo = customer.company_logo || customer.logo_url;
                if (existingLogo.startsWith('http') || existingLogo.startsWith('/')) {
                    formData.append('company_logo', existingLogo);
                } else {
                    // Handle relative paths
                    const backendUrl = process.env.REACT_APP_ROOT_URL || 'http://localhost:8000';
                    formData.append('company_logo', `${backendUrl.replace('/api/v1', '')}/static/${existingLogo.replace('uploads/', '')}`);
                }
            }
            
            if (onUpdate) {
                await onUpdate(formData);
                // Refresh licenses after update
                await fetchCustomerLicenses();
                setIsEditing(false);
            }
        } catch (error) {
            console.error('Error updating customer:', error);
            toast.error(error.response?.data?.message || 'Failed to update customer');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        reset();
        setIsEditing(false);
        // Reset logo preview to original
        setLogoPreviewUrl(customer?.company_logo || '');
        setLogoFile(null);
        setLogoError(!customer?.company_logo);
    };



    const handleBack = () => {
        navigate(-1); // Go back to the previous page
    };

    if (!customer) {
        return (
            <div className="alert alert-warning">
                No customer data available.
            </div>
        );
    }

    return (
        <div className="row gy-4">
            
            {/* Customer Info Card - Left Column */}
            <div className="col-lg-4">
                <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100">
                    <img
                        src="/assets/images/card/card-bg.png"
                        alt="Background"
                        className="w-100 object-fit-cover"
                    />
                    <div className="pb-24 ms-16 mb-24 me-16 mt--100">
                        <div className="text-center border border-top-0 border-start-0 border-end-0">
                            <img
                                src={logoPreviewUrl || customer?.company_logo}
                                alt="Company Logo"
                                className="border br-white border-width-2-px w-200-px h-200-px rounded-circle object-fit-cover"
                            />
                            <h6 className="mb-0 mt-16">{customer?.company_name || 'N/A'}</h6>
                            <span className="text-secondary-light mb-16">{customer?.company_email || 'N/A'}</span>
                        </div>
                        <div className="mt-24">
                            <h6 className="text-xl mb-16">Company Info</h6>
                            <ul>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Company
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {customer?.company_name || 'N/A'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Email
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {customer?.company_email || 'N/A'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Phone
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {customer?.company_phone || 'N/A'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Industry
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {customer?.industry || 'N/A'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Website
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {customer?.website ? (
                                            <a href={`https://${customer.website.replace(/^https?:\/\//, '')}`} target="_blank" rel="noopener noreferrer">
                                                {customer.website}
                                            </a>
                                        ) : 'N/A'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Status
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : <span className={`badge ${customer?.status === 'active' ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'}`}>
                                            {customer?.status === 'active' ? 'Active' : 'Inactive'}
                                          </span>
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Contact Person
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {customer?.contact_name || 'N/A'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Contact Email
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {customer?.contact_email ? (
                                            <a href={`mailto:${customer.contact_email}`}>
                                                {customer.contact_email}
                                            </a>
                                        ) : 'N/A'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Contact Phone
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {customer?.contact_phone || 'N/A'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Job Title
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {customer?.job_title || 'N/A'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-start gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Address
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {[
                                            customer?.street_address,
                                            customer?.city,
                                            customer?.state_province,
                                            customer?.postal_code,
                                            customer?.country
                                          ].filter(Boolean).join(', ') || 'N/A'}
                                    </span>
                                </li>
                                {customer?.notes && (
                                <li className="d-flex align-items-start gap-1">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Notes
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {customer.notes}
                                    </span>
                                </li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Tabs Section - Right Column */}
            <div className="col-lg-8">
                <div className="card h-100">
                    <div className="card-body p-24">
                        <div className="d-flex align-items-center justify-content-between gap-10">
                            <ul
                                className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
                                id="pills-tab"
                                role="tablist" >
                                <li className="nav-item" role="presentation">
                                    <button
                                        className="nav-link d-flex align-items-center px-24 active"
                                        id="pills-edit-company-tab"
                                        data-bs-toggle="pill"
                                        data-bs-target="#pills-edit-company"
                                        type="button"
                                        role="tab"
                                        aria-controls="pills-edit-company"
                                        aria-selected="true"
                                    >
                                        Edit Company
                                    </button>
                                </li>

                                <li className="nav-item" role="presentation">
                                    <button
                                        className="nav-link d-flex align-items-center px-24"
                                        id="pills-licenses-tab"
                                        data-bs-toggle="pill"
                                        data-bs-target="#pills-licenses"
                                        type="button"
                                        role="tab"
                                        aria-controls="pills-licenses"
                                        aria-selected="false"
                                        tabIndex={-1}
                                    >
                                        Licenses
                                    </button>
                                </li>
                            </ul>

                            <button 
                                onClick={handleBack}
                                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                            >
                                <Icon icon="mdi:arrow-left" className="icon" />
                                Back to Customers
                            </button>
                        </div>
                        
                        <div className="tab-content" id="pills-tabContent">
                            {/* Edit Company Tab */}
                            <div
                                className="tab-pane fade show active"
                                id="pills-edit-company"
                                role="tabpanel"
                                aria-labelledby="pills-edit-company-tab"
                                tabIndex={0}
                            >
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="d-flex justify-content-between align-items-center mb-24">
                                        <h6 className="text-md text-primary-light mb-0">Company Information</h6>
                                        {user.role !== 'USER' && (
                                            !isEditing ? (
                                                <button 
                                                    type="button" 
                                                    className="btn btn-primary"
                                                    onClick={handleEditClick}
                                                >
                                                    <i className="ri-edit-line me-2"></i> Edit
                                                </button>
                                            ) : (
                                                <div className="d-flex gap-2">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-outline-secondary"
                                                        onClick={handleCancel}
                                                        disabled={isSubmitting}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button 
                                                        type="submit" 
                                                        className="btn btn-primary"
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                    
                                    <div className="mb-24">
                                        <h6 className="text-md text-primary-light mb-3">Company Logo</h6>
                                        <div className="d-flex flex-column align-items-center">
                                            <div className="position-relative mb-3">
                                                <div 
                                                    className="rounded-circle overflow-hidden"
                                                    style={{
                                                        width: '120px',
                                                        height: '120px',
                                                        border: '2px solid #e9ecef',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#f8f9fa'
                                                    }}
                                                >
                                                    {logoError || !logoPreviewUrl ?  (
                                                        <div className="d-flex flex-column align-items-center justify-content-center h-100 w-100">
                                                            <Icon icon="mdi:office-building" className="text-muted" style={{ fontSize: '3rem' }} />
                                                            <small className="text-muted mt-2">No logo</small>
                                                        </div>
                                                    ) : (
                                                        <img
                                                            src={logoPreviewUrl}
                                                            alt="Company Logo"
                                                            className="img-fluid"
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: '100%',
                                                                objectFit: 'contain'
                                                            }}
                                                            onError={() => setLogoError(true)}
                                                        />
                                                    )}
                                                </div>
                                                {isEditing && (
                                                    <div className="position-absolute bottom-0 end-0">
                                                        <label 
                                                            className="btn btn-sm btn-primary rounded-circle p-2"
                                                            style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Change logo"
                                                        >
                                                            <Icon icon="mdi:camera" className="fs-5" />
                                                            <input 
                                                                type="file" 
                                                                className="d-none" 
                                                                accept="image/png, image/jpeg, image/jpg, image/gif"
                                                                onChange={handleLogoChange}
                                                            />
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                            {isEditing && (
                                                <small className="text-muted">
                                                    Click the camera icon to change the logo
                                                </small>
                                            )}
                                        </div>
                                        
                                        <div className="row g-4 mb-24">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Company Name <span className="text-danger">*</span></label>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.company_name ? 'is-invalid' : ''}`}
                                                        {...register('company_name', { required: 'Company name is required' })}
                                                        disabled={!isEditing}
                                                    />
                                                    {errors.company_name && (
                                                        <div className="invalid-feedback">{errors.company_name.message}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Company Email <span className="text-danger">*</span></label>
                                                    <input
                                                        type="email"
                                                        className={`form-control ${errors.company_email ? 'is-invalid' : ''}`}
                                                        {...register('company_email', {
                                                            required: 'Company email is required',
                                                            pattern: {
                                                                value: /^\S+@\S+\.\S+$/,
                                                                message: 'Please enter a valid email address'
                                                            }
                                                        })}
                                                        disabled={!isEditing}
                                                    />
                                                    {errors.company_email && (
                                                        <div className="invalid-feedback">{errors.company_email.message}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Company Phone</label>
                                                    <input
                                                        type="tel"
                                                        className={`form-control ${errors.company_phone ? 'is-invalid' : ''}`}
                                                        {...register('company_phone', {
                                                            pattern: {
                                                                value: /^[\d\s\-+()]*$/,
                                                                message: 'Please enter a valid phone number'
                                                            }
                                                        })}
                                                        disabled={!isEditing}
                                                    />
                                                    {errors.company_phone && (
                                                        <div className="invalid-feedback">{errors.company_phone.message}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Industry</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        {...register('industry')}
                                                        disabled={!isEditing}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Website</label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">https://</span>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.website ? 'is-invalid' : ''}`}
                                                            // {...register('website', {
                                                            //     pattern: {
                                                            //         value: /^(?!https?:\/\/)[^\s]+\.[^\s]+$/,
                                                            //         message: 'Please enter a valid website (without http:// or https://)'
                                                            //     }
                                                            // })}
                                                            disabled={!isEditing}
                                                            placeholder="example.com"
                                                        />
                                                    </div>
                                                    {errors.website && (
                                                        <div className="invalid-feedback d-block">{errors.website.message}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Status</label>
                                                    <select
                                                        className="form-select"
                                                        {...register('status')}
                                                        disabled={!isEditing}
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </select>
                                                </div>
                                            </div>
                                            
                                            <div className="col-12 mt-4">
                                                <h6 className="text-md text-primary-light mb-3">Contact Information</h6>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Contact Name <span className="text-danger">*</span></label>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.contact_name ? 'is-invalid' : ''}`}
                                                        {...register('contact_name', { required: 'Contact name is required' })}
                                                        disabled={!isEditing}
                                                    />
                                                    {errors.contact_name && (
                                                        <div className="invalid-feedback">{errors.contact_name.message}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Job Title</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        {...register('job_title')}
                                                        disabled={!isEditing}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Contact Email</label>
                                                    <input
                                                        type="email"
                                                        className={`form-control ${errors.contact_email ? 'is-invalid' : ''}`}
                                                        {...register('contact_email', {
                                                            pattern: {
                                                                value: /^\S+@\S+\.\S+$/,
                                                                message: 'Please enter a valid email address'
                                                            }
                                                        })}
                                                        disabled={!isEditing}
                                                    />
                                                    {errors.contact_email && (
                                                        <div className="invalid-feedback">{errors.contact_email.message}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Contact Phone</label>
                                                    <input
                                                        type="tel"
                                                        className={`form-control ${errors.contact_phone ? 'is-invalid' : ''}`}
                                                        {...register('contact_phone', {
                                                            pattern: {
                                                                value: /^[\d\s\-+()]*$/,
                                                                message: 'Please enter a valid phone number'
                                                            }
                                                        })}
                                                        disabled={!isEditing}
                                                    />
                                                    {errors.contact_phone && (
                                                        <div className="invalid-feedback">{errors.contact_phone.message}</div>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="col-12 mt-4">
                                                <h6 className="text-md text-primary-light mb-3">Address</h6>
                                            </div>
                                            
                                            <div className="col-12">
                                                <div className="form-group">
                                                    <label className="form-label">Street Address</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        {...register('street_address')}
                                                        disabled={!isEditing}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">City</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        {...register('city')}
                                                        disabled={!isEditing}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">State/Province</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        {...register('state_province')}
                                                        disabled={!isEditing}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Postal Code</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        {...register('postal_code')}
                                                        disabled={!isEditing}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Country</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        {...register('country')}
                                                        disabled={!isEditing}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="col-12">
                                                <div className="form-group">
                                                    <label className="form-label">Notes</label>
                                                    <textarea
                                                        className="form-control"
                                                        rows="3"
                                                        {...register('notes')}
                                                        disabled={!isEditing}
                                                    ></textarea>
                                                </div>
                                            </div>
                                        </div>

                                        
                                    </div>
                                </form>
                            </div>
                            

                            {/* Licenses Tab */}
                            <div
                                className="tab-pane fade"
                                id="pills-licenses"
                                role="tabpanel"
                                aria-labelledby="pills-licenses-tab"
                                tabIndex={0}
                            >
                                <div className="p-3">
                                <h6 className="text-md text-primary-light mb-4">Licenses</h6>
                                {loading ? (
                                    <div className="text-center p-4">
                                        <Spin size="large" />
                                        <p className="mt-2">Loading licenses...</p>
                                    </div>
                                ) : error ? (
                                    <div className="alert alert-danger">
                                        {error}
                                    </div>
                                ) : licenses.length === 0 ? (
                                    <Empty 
                                        description={
                                            <span>No licenses found for this customer</span>
                                        }
                                    />
                                ) : (
                                    <div className="table-responsive">
                                        <Table 
                                            dataSource={licenses}
                                            rowKey="id"
                                            pagination={false}
                                            size="small"
                                            columns={[
                                                {
                                                    title: 'License Key',
                                                    dataIndex: 'license_key',
                                                    key: 'license_key',
                                                    render: (text) => (
                                                        <code>{text}</code>
                                                    )
                                                },
                                                {
                                                    title: 'Type',
                                                    dataIndex: 'license_type',
                                                    key: 'type',
                                                    render: (type) => {
                                                        const typeInfo = LICENSE_TYPES[type] || { label: type, color: 'default' };
                                                        return (
                                                            <Tag color={typeInfo.color}>
                                                                {typeInfo.label || type}
                                                            </Tag>
                                                        );
                                                    }
                                                },
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    render: (status) => {
                                                        let color = 'default';
                                                        switch (status) {
                                                            case 'ACTIVE':
                                                                color = 'green';
                                                                break;
                                                            case 'EXPIRED':
                                                                color = 'red';
                                                                break;
                                                            case 'REVOKED':
                                                                color = 'red';
                                                                break;
                                                            case 'PENDING':
                                                                color = 'blue';
                                                                break;
                                                            case 'SUSPENDED':
                                                                color = 'orange';
                                                                break;
                                                            default:
                                                                color = 'default';
                                                        }
                                                        return (
                                                            <Tag color={color}>
                                                                {status.charAt(0) + status.slice(1).toLowerCase()}
                                                            </Tag>
                                                        );
                                                    }
                                                },
                                                {
                                                    title: 'Expires',
                                                    dataIndex: 'expiry_date',
                                                    key: 'expiry',
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : 'Never'
                                                },
                                                {
                                                    title: 'Max Users',
                                                    dataIndex: 'max_users',
                                                    key: 'max_users',
                                                    align: 'right'
                                                }
                                            ]}
                                        />
                                    </div>
                                )}
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
